import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageSquare, Send, User, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (ts) => {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchMessages();
    // Mark as read on page visit
    axios.post(`${API_URL}/messages/mark-read`).catch(() => {});
    const interval = setInterval(fetchMessages, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/messages`);
      setMessages(response.data.reverse()); // oldest first for chat view
    } catch (error) {
      console.error("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/messages`, { message: newMessage });
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/messages/${messageId}`);
      toast.success("Message deleted");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading messages...</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8 h-[calc(100vh-56px)] lg:h-screen flex flex-col" data-testid="messages-page">
      <div className="mb-4">
        <h1 className="text-2xl md:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: "-0.02em" }}>Messages</h1>
        <p className="text-slate-500 mt-1 text-sm">Team announcements & instructions</p>
      </div>

      <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-sm overflow-hidden min-h-0">
        {/* Message area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="message-list">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <MessageSquare className="text-slate-200 mb-4" size={64} />
              <p className="text-slate-400 text-lg font-medium">No messages yet</p>
              {isAdmin && <p className="text-slate-400 text-sm mt-1">Send the first message to your team</p>}
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
                  <div className={`max-w-[75%] md:max-w-[60%] ${isOwn ? "order-2" : ""}`}>
                    <div className={`flex items-center gap-2 mb-1 ${isOwn ? "justify-end" : ""}`}>
                      {!isOwn && (
                        <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                          <User size={14} className="text-white" />
                        </div>
                      )}
                      <span className="text-xs font-semibold text-slate-600">{msg.sender_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-sm font-bold uppercase ${msg.sender_role === "admin" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}`} style={{ fontSize: "10px" }}>{msg.sender_role}</span>
                      <span className="text-xs text-slate-400">{timeAgo(msg.created_at)}</span>
                    </div>
                    <div className={`px-4 py-3 rounded-lg ${isOwn ? "bg-slate-900 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"} group relative`}>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className={`absolute -top-2 -right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${isOwn ? "bg-red-500 text-white hover:bg-red-600" : "bg-red-100 text-red-600 hover:bg-red-200"}`}
                          title="Delete message"
                          data-testid={`delete-msg-${msg.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Compose area */}
        {isAdmin ? (
          <div className="border-t border-slate-200 p-4 bg-slate-50">
            <form onSubmit={handleSend} className="flex gap-3 items-end">
              <Textarea
                data-testid="message-input"
                placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                className="flex-1 rounded-sm border-slate-300 focus:border-orange-500 bg-white resize-none"
              />
              <Button type="submit" disabled={sending || !newMessage.trim()} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm h-10 px-5" data-testid="send-message-button">
                <Send size={18} />
              </Button>
            </form>
          </div>
        ) : (
          <div className="border-t border-slate-200 p-3 bg-slate-50 text-center">
            <p className="text-xs text-slate-500">Only admins can send messages</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
